import { CdkMenuTrigger } from '@angular/cdk/menu';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  OverlayRef,
} from '@angular/cdk/overlay';

// `overlayRef` is `protected` on CdkMenuTriggerBase, but `OverlayRef.updatePosition()`
// itself is public. Quarantining the casts here keeps the CDK-internal coupling
// in one file, so a future CDK rename only touches these lines.
type WithOverlay = { overlayRef?: OverlayRef };
type StrategyInternals = {
  _lastPosition?: ConnectedPosition;
  _preferredPositions?: ConnectedPosition[];
};

// Snapshot of the strategy's original preferred-position list, captured the
// first time we reposition each overlay. Restored when the overlay detaches so
// subsequent opens evaluate position freshly against the current viewport.
const originalPositions = new WeakMap<OverlayRef, ConnectedPosition[]>();

export function repositionCdkMenuTrigger(trigger: CdkMenuTrigger | undefined): void {
  const overlayRef = (trigger as unknown as WithOverlay | undefined)?.overlayRef;
  if (!overlayRef) {
    return;
  }

  const strategy = overlayRef.getConfig().positionStrategy;
  if (!(strategy instanceof FlexibleConnectedPositionStrategy)) {
    return;
  }

  const internals = strategy as unknown as StrategyInternals;

  // CDK's menu trigger configures the strategy with `withLockedPosition()` and
  // `_growAfterOpen = false`. Lock alone makes `updatePosition()` skip the push
  // path; growAfterOpen=false additionally clamps the bounding-box height to
  // whatever it was at first render, so even when push *does* run it acts on
  // a stale rect and barely shifts. Together those defaults leave the menu
  // clipped when its content grows. Unlocking fixes push but lets CDK flip
  // orientation (above ↔ below) when content shrinks — jarring mid-interaction.
  // The fix is to pin the preferred-position list to whichever pair CDK chose
  // at open (no flip), and enable growAfterOpen so the bounding box tracks the
  // current overlay size (push can do its job). Originals restored on detach.
  if (!originalPositions.has(overlayRef) && internals._preferredPositions) {
    originalPositions.set(overlayRef, [...internals._preferredPositions]);
    // detachments() completes on dispose, so no manual cleanup is needed.
    overlayRef.detachments().subscribe(() => {
      const originals = originalPositions.get(overlayRef);
      if (originals) {
        strategy.withPositions(originals);
        originalPositions.delete(overlayRef);
      }
    });
  }

  const lastPosition = internals._lastPosition;
  if (lastPosition) {
    strategy
      .withPositions([lastPosition])
      .withLockedPosition(false)
      .withPush(true)
      .withGrowAfterOpen(true);
  }

  overlayRef.updatePosition();
}
